import React from 'react';
import styled, { css } from 'styled-components';

import BreadCrumbs from '@/components/atom/BreadCrumbs';
import TemplateLayout from '@/components/layouts/TemplateLayout';
import StickyAppHeader from '@/components/molecules/StickyAppHeader';
import useHospitalHomeViewModel from '@/hooks/hospital/main/useHospitalHomeViewModel';
import { ClientType, CustomPageProps } from '@/types/app';
import { ROUTES } from '@/types/common';

import FindHospitalCategories from './FindHospitalCategories';
import FindHospitalTreatments from './FindHospitalTreatments';
import MedicalCategoriesModal from './MedicalCategoriesModal';

const HospitalsHomeTemplate = ({ client }: CustomPageProps) => {
  const viewModel = useHospitalHomeViewModel({ client });

  return (
    <TemplateLayout.Wrapper>
      {client.isWebView && (
        <TemplateLayout.StickyHeader>
          <StickyAppHeader title={'가까운 병원 찾기'} />
        </TemplateLayout.StickyHeader>
      )}
      <TemplateLayout.Content>
        <BreadCrumbsWrapper>
          <BreadCrumbs visible={!client.isMobile} type='hospitals' path={`${ROUTES.HOSPITAL.HOME.path}`} />
        </BreadCrumbsWrapper>
        {client.clientType === ClientType.WEB && <FindHospitalTitle>가까운 병원 찾기</FindHospitalTitle>}
        <FindHospitalCategories>
          <FindHospitalCategories.Search
            onSubmit={(keyword) => viewModel.navigateToSearchHospitalsFromKeyword(keyword)}
          />
          <FindHospitalCategories.Title text='어떤 병원을 찾으세요?' />
          <FindHospitalCategories.SupportText text='예약하고 대기없이 편하게 방문해보세요' />
          <FindHospitalCategories.Categories>
            {viewModel.categories.map((category) => (
              <FindHospitalCategories.Category
                key={`hospital-category-${category.id}}`}
                category={category}
                isMobile={client.isMobile}
                onClick={() => viewModel.clickCategory(category)}
              />
            ))}
            <FindHospitalCategories.MoreSee text={'전체보기'} onClick={viewModel.openMedicalCategories} />
          </FindHospitalCategories.Categories>
        </FindHospitalCategories>
        <Division />
        <FindHospitalTreatments>
          <FindHospitalTreatments.Title text='어떻게 아프세요?' />
          <FindHospitalTreatments.Treatments>
            {viewModel.treatments.map((treatment) => (
              <FindHospitalTreatments.Treatment
                key={`hospital-treatment-${treatment.id}`}
                text={`${treatment.name}`}
                onClick={() => viewModel.navigateToNearSearchHospitalsFromKeyword(treatment.name, 'treatment')}
              />
            ))}
          </FindHospitalTreatments.Treatments>
        </FindHospitalTreatments>
      </TemplateLayout.Content>
      {!client.isMobile && (
        <>
          {viewModel.isCategoriesModalOpen && (
            <MedicalCategoriesModal
              client={client}
              categories={viewModel.allCategories}
              onClick={(keyword) => viewModel.navigateToNearSearchHospitalsFromKeyword(keyword, 'category')}
              toggleModal={viewModel.closeMedicalCategories}
            />
          )}
        </>
      )}
    </TemplateLayout.Wrapper>
  );
};

export default HospitalsHomeTemplate;

const BreadCrumbsWrapper = styled.div`
  padding: ${({ theme }) => (theme.client.isMobile ? '0' : '24px 24px 0')};
`;

const FindHospitalTitle = styled.h1`
  ${({ theme }) => css`
    ${theme.typography.UI22SB}
    color: ${theme.color.gray900};
    display: flex;
    align-items: center;
    padding: 0 24px;
    height: 60px;
  `}
`;

const Division = styled.div`
  ${({ theme }) => css`
    width: 100%;
    height: 16px;
    background-color: ${theme.color.gray30};
  `}
`;
